import React, {useCallback, useEffect, useRef, useState} from "react";
import Box from "@mui/material/Box";
import {Divider, IconButton, InputAdornment, SwipeableDrawer, TextField} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {debounce} from "lodash";
import {useDispatch, useSelector} from "react-redux";
import {filterRestaurants} from "./search-util";
import PaginatedList from "./PaginatedList";
import Typography from "@mui/material/Typography";
import TitleBarBelowImageList from "../Card/TitleBarBelowImageList";
import FeaturedBusiness from "../Card/FeaturedBusiness";
import ClearIcon from "@mui/icons-material/Clear";
import { setModalOpen, setModalRestaurant } from "../../redux/features/infoModal/infoModalSlice";
import LocationOnIcon from "@mui/icons-material/LocationOn";



const SearchMobile = ({isMenuOpen, setMenuOpen}) => {
    const allRestaurants = useSelector((state) => state.restaurantsReducer.restaurants);
    const nearbyRestaurants = useSelector((state) => state.restaurantsReducer.nearbyRestaurants);
    const searchRef = useRef(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const dispatch = useDispatch();
    const iOS = typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent);
    const restaurantsToUse = nearbyRestaurants.length > 0 ? nearbyRestaurants : allRestaurants;
    const [sortBy, setSortBy] = useState('relevance'); // 'relevance' or 'proximity'
    const [hasUserLocation, setHasUserLocation] = useState(false);

    useEffect(() => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(() => {
                setHasUserLocation(true);
            });
        }
    }, []);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedHandleSearch = useCallback(debounce((term, sortOption = sortBy) => {
        let searchString = term.toLowerCase().trim();
        if (searchString === "") {
            setSearchResults([]);
            return;
        }

        let filteredResults = filterRestaurants(restaurantsToUse, searchString);
        if (sortOption === 'proximity') {
            filteredResults = [...filteredResults].sort((a, b) => {
                if (a.distance == null) return 1;
                if (b.distance == null) return -1;
                return a.distance - b.distance;
            });
        }
        setSearchResults(filteredResults);
    }, 300), [nearbyRestaurants, allRestaurants, sortBy]);

    useEffect(() => {
        if (isMenuOpen && searchRef.current) {
            searchRef.current.focus();
        }
    }, [isMenuOpen]);

    const handleSearch = (event, sortOption = sortBy) => {
        debouncedHandleSearch(event, sortOption);
    };

    const onItemClick = (restaurant) => {
        dispatch(setModalRestaurant(restaurant));
        dispatch(setModalOpen(true));
    }

    const onStaffPickClick = (restaurantId) => {
        const restaurant = allRestaurants.find((restaurant) => restaurant.id === restaurantId);
        dispatch(setModalRestaurant(restaurant));
        dispatch(setModalOpen(true));
    }

    return (
        <>
            <SwipeableDrawer
                open={isMenuOpen}
                onOpen={() => setMenuOpen(true)}
                onClose={() => {
                    setMenuOpen(false);
                }}
                anchor={'right'}
                disableDiscovery={iOS} // iOS has a "swipe to go back" feature that interferes with the discovery feature, so discovery has to be disabled.
            >
                <Box sx={{ display: 'flex', alignItems: 'center', padding: 2 }}>
                    <ArrowBackIcon
                        sx={{ cursor: 'pointer', marginRight: 1, color: '#00C2FF' }}
                        onClick={() => setMenuOpen(false)}
                    />
                    <TextField
                        variant="outlined"
                        placeholder="Search..."
                        fullWidth
                        inputRef={searchRef}
                        sx={{
                            borderRadius: 1,
                        }}
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            handleSearch(e.target.value);
                        }}
                        InputProps={{
                            endAdornment: searchTerm && (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => {
                                            setSearchTerm('');
                                            handleSearch('');
                                        }}
                                        edge="end"
                                    >
                                        <ClearIcon />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    {hasUserLocation && (
                        <IconButton
                            onClick={() => {
                                const newSort = sortBy === 'proximity' ? 'relevance' : 'proximity';
                                setSortBy(newSort);
                                handleSearch(searchTerm, newSort);
                            }}
                            sx={{
                                marginLeft: 2,
                                color: sortBy === 'proximity' ? '#00C2FF' : 'grey.500',
                                backgroundColor: sortBy === 'proximity' ? 'rgba(0,194,255,0.1)' : 'transparent',
                                borderRadius: '50%',
                                transition: 'background 0.2s',
                            }}
                        >
                            <LocationOnIcon />
                        </IconButton>
                    )}
                </Box>
                <Divider/>
                <PaginatedList searchResults={searchResults} itemsPerPage={7} onItemClick={onItemClick}/>
                {/*Below Section*/}
                <FeaturedBusiness onClick={onStaffPickClick} />
                <Divider sx={{ mt: 2 }} />
                <Box sx={{display: 'flex', flexDirection: 'column', width: '100vw', paddingTop: 3}}>
                    <Box sx={{mt: 'auto'}}>
                        <Typography
                            variant="subtitle1"
                            color="#00C2FF"
                            fontWeight="bold"
                            gutterBottom
                            sx={{ paddingLeft: 2 }}
                        >
                            Staff Picks
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                    </Box>
                </Box>
                <TitleBarBelowImageList itemOnClick={onStaffPickClick}/>
            </SwipeableDrawer>
        </>
    )
};

export default SearchMobile;
